// src/pages/EmployeeProfilesIndex.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Header from '../Header';
import { listTeamProfiles } from '../../api/employeeProfiles';
import { useNavigate } from 'react-router-dom';
import '../../components/reviews/reviews-table.css';

const SORTABLE = [
    { key: 'employee_number', label: 'Employee #' },
    { key: 'job_title',       label: 'Job Title' },
    { key: 'division',        label: 'Division' },
    { key: 'department',      label: 'Department' },
    { key: 'name',            label: 'Name' },
    { key: 'created_at',      label: 'Created' },
];

export default function EmployeeProfilesIndex() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [manager, setManager] = useState(null);
    const [rows, setRows] = useState([]);

    // server paging/sort/search state
    const [q, setQ] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage] = useState(15);
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });

    async function load() {
        try {
            setLoading(true); setErr('');
            const res = await listTeamProfiles({
                q: q || undefined,
                sort_by: sortBy,
                sort_dir: sortDir,
                page,
                per_page: perPage,
            });

            setManager(res.manager || null);

            // Laravel paginator style
            const p = res.data || {};
            setRows(Array.isArray(p.data) ? p.data : []);
            setMeta({
                current_page: p.current_page ?? 1,
                last_page: p.last_page ?? 1,
                total: p.total ?? 0,
            });
        } catch (e) {
            setErr(e?.response?.data?.message || 'Failed to load team profiles.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, sortBy, sortDir, page]);

    const onHeaderClick = (key) => {
        if (key === sortBy) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(key);
            setSortDir('asc');
        }
    };

    const sortIcon = (key) => {
        if (key !== sortBy) return null;
        return <span className="ms-1">{sortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    const canPrev = meta.current_page > 1;
    const canNext = meta.current_page < meta.last_page;

    return (
        <>
            <Header label={<p className="py-0 my-0">Team Profiles</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">

                        <div className="card">
                            <div className="card-header d-flex align-items-center gap-2">
                                <div className="d-flex flex-column">
                                    <h5 className="mb-0">Direct Reports</h5>
                                    <div className="text-tertiary small ms-0">
                                        {manager ? `Manager: ${manager.name} ` : ''}
                                    </div>
                                </div>

                                <div className="ms-auto d-flex gap-2">
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="Search by name, email or employee number…"
                                        style={{ width: 340 }}
                                        value={q}
                                        onChange={e => { setPage(1); setQ(e.target.value); }}
                                    />
                                </div>
                            </div>

                            <div className="card-body">
                                {err && <div className="alert alert-danger">{err}</div>}

                                {loading ? (
                                    <div className="text-muted">Loading…</div>
                                ) : (
                                    <>
                                        <div className="table-responsive reviews-table reviews-table--zebra">
                                            <table>
                                                <thead>
                                                <tr className="rt-head-top">
                                                    <th className="rt-counter">#</th>
                                                    <th className="rt-name">
                                                        <button type="button" className="btn btn-link p-0 text-decoration-none text-white font-weight-bold"
                                                                onClick={() => onHeaderClick('employee_number')}>
                                                            Employee #
                                                            {sortIcon('employee_number')}
                                                        </button>
                                                    </th>

                                                    <th className="rt-counter">Name & Surname</th>
                                                    <th className="rt-counter">Email</th>
                                                    <th>
                                                        <button type="button" className="btn btn-link p-0 text-decoration-none text-white font-weight-bold"
                                                                onClick={() => onHeaderClick('job_title')}>
                                                            Job Title
                                                            {sortIcon('job_title')}
                                                        </button>
                                                    </th>
                                                    <th>
                                                        <button type="button" className="btn btn-link p-0 text-decoration-none text-white font-weight-bold"
                                                                onClick={() => onHeaderClick('division')}>
                                                            Division
                                                            {sortIcon('division')}
                                                        </button>
                                                    </th>
                                                    <th>
                                                        <button type="button" className="btn btn-link p-0 text-decoration-none text-white font-weight-bold"
                                                                onClick={() => onHeaderClick('department')}>
                                                            Department
                                                            {sortIcon('department')}
                                                        </button>
                                                    </th>
                                                    <th className="rt-actions">Action</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {rows.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="text-center text-muted py-4">No employees found.</td>
                                                    </tr>
                                                )}
                                                {rows.map((r, idx) => (
                                                    <tr key={r.user_id}>
                                                        <td className="rt-counter">{String((meta.current_page - 1) * perPage + idx + 1).padStart(2,'0')}</td>
                                                        <td className="rt-name">{r.employee_number ?? '—'}</td>
                                                        <td className="rt-name">{r.name ?? '—'}</td>
                                                        <td className="rt-name">{r.email ?? '—'}</td>
                                                        <td>{r.job_title ?? '—'}</td>
                                                        <td>{r.division ?? '—'}</td>
                                                        <td>{r.department ?? '—'}</td>
                                                        <td className="rt-actions text-end">
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => navigate(`/employee-profiles/${r.user_id}`)}
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="text-muted small">
                                                Page {meta.current_page} of {meta.last_page} • Total {meta.total}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={!canPrev}
                                                    onClick={() => canPrev && setPage(p => p - 1)}
                                                >
                                                    Prev
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={!canNext}
                                                    onClick={() => canNext && setPage(p => p + 1)}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </main>
        </>
    );
}